/**
 * ForecastDealDetailTable — the artifact's Deal Detail table, live.
 *
 * COLUMN MODEL (05.3 D-13b; first header 'Opps' per quick-260917-nfy, the
 * artifact's wording): Opps · Type · Close · Amount · Position. Five columns,
 * and deliberately no per-deal forecast figure. Commit and Most Likely
 * bank at 100% and Best Case is the viewer's Upside % toggle, so a weighted
 * figure printed per row would only restate the Amount beside it. The toggle's
 * effect is shown on the TOTAL alone — which is the footer's job.
 *
 * FOOTER BASIS (05.3 D-03 / D-13b). Subtotal, budget and variance all compute on
 * ONE basis: the section's forecast call at the LIVE Upside %, via
 * `split(deals, upsidePct).total` from `lib/forecast-weighting.ts`. This
 * component previously derived its footer from the payload's server-baked
 * per-deal weight, which is frozen at the stored conversion grid at recompute
 * time and therefore cannot move with a client-side toggle (05.3-RESEARCH
 * Finding 1). Leaving that in place while the subtotal reported a raw amount
 * total produced the specific defect this file was rewritten to close: a
 * variance row comparing a RAW total against the ARR budget, sitting directly
 * beside an equation strip showing a WEIGHTED figure, the two disagreeing by
 * however far the viewer had moved the knob. Nothing here reads that field.
 *
 * `upsidePct` is a REQUIRED prop supplied by the page (D-03 — the value is a
 * per-viewer viewing lens, never written back). `ForecastSectionCard` forwards
 * it; the page is its sole supplier.
 *
 * BANDING (05.3 D-13): Closed-Won rows are banded and nothing else is. The
 * committed / upside / totals palette stays in the exported workbook, not in the
 * browser. The contract is pinned as `data-band="closed_won"` so the behaviour
 * is locked without freezing the styling.
 *
 * GROUPING (05.3 D-15 / 05.1 D-21): Committed (Closed-Won + Commit + Most
 * Likely), then Upside (Best Case), then the zero-weighted deals — Pipeline and
 * the server-normalised 'No position' label — as their own trailing group. They
 * are RENDERED, never filtered: a viewer must be able to see a large deal
 * contributing nothing rather than wonder where it went (05.1 D-22).
 *
 * ACCOUNT ON MANAGED ROWS (quick-261015-lej, John: "make sure the deals under
 * managed services have the account name listed"): a Managed Services row's
 * first cell reads "Account — deal name" through the shared
 * `dealAccountPrefix` rule, the same read the CEO artifact prints. Product rows
 * are unchanged; a Managed name that already carries its account is not
 * prefixed twice. Still five columns.
 */
import { dealAccountPrefix } from '@saledash/shared/forecast/workbook';

import { split } from '../../lib/forecast-weighting.js';
import { formatAmount, formatCloseDate, signed } from '../../lib/format.js';

import type { ForecastDeal } from '@saledash/shared';
import type { UpsidePct } from '@saledash/shared/forecast/weighting';

/**
 * Position labels, kept as literals rather than imported from the weighting
 * module because these drive GROUPING (a stable, toggle-independent read of the
 * table) while the module owns the dollar WEIGHTS.
 */
const CLOSED_WON_LABEL = 'Closed Won';
const BEST_CASE = 'Best Case';
const COMMIT_POSITIONS: readonly string[] = ['Commit', 'Most Likely'];

/** Banks at 1.00 as already-won revenue — the only banded row on the page. */
const isWon = (d: ForecastDeal): boolean =>
  d.tier === 'closed_won' || d.forecast_position === CLOSED_WON_LABEL;

const isCommitted = (d: ForecastDeal): boolean =>
  isWon(d) || COMMIT_POSITIONS.includes(d.forecast_position);

const isUpside = (d: ForecastDeal): boolean =>
  !isCommitted(d) && d.forecast_position === BEST_CASE;

/** Short type label: New Business → New, Existing Business → Expansion. */
function typeLabel(type: string | null): string {
  if (type === 'New Business') return 'New';
  if (type === 'Existing Business') return 'Expansion';
  return type ?? '—';
}

/**
 * quick-260921-f73 — ascending by CLOSE DATE within each tier, earliest first
 * (John: "all deal tables should be sorted by close date, early to later"),
 * matching the artifact's `byDate`. Deals with no close date sink to the end of
 * their tier; ties fall back to raw amount, largest first. Nothing here reads a
 * toggle-dependent value, so the order never reshuffles as the Upside % moves.
 */
function byCloseDateAsc(deals: readonly ForecastDeal[]): ForecastDeal[] {
  return [...deals].sort((a, b) => {
    if (a.close_date === b.close_date) return b.raw_amount - a.raw_amount;
    if (a.close_date === null) return 1;
    if (b.close_date === null) return -1;
    return a.close_date < b.close_date ? -1 : 1;
  });
}

interface ForecastDealDetailTableProps {
  sectionKey: string;
  deals: ForecastDeal[];
  /** Section budget target (budget / variance rows); null ⇒ those rows omitted. */
  target: number | null;
  /**
   * The viewer's Upside % (D-03). Supplied by the page — this component never
   * sources a default of its own, so there is exactly one live value on the tab.
   */
  upsidePct: UpsidePct;
}

export function ForecastDealDetailTable({
  sectionKey,
  deals,
  target,
  upsidePct,
}: ForecastDealDetailTableProps): React.JSX.Element {
  if (deals.length === 0) {
    return (
      <p
        className="py-8 text-center text-sm text-ink-500"
        data-testid={`forecast-dealdetail-empty-${sectionKey}`}
      >
        No open deals this period.
      </p>
    );
  }

  const groups = [
    { label: 'COMMITTED', slug: 'committed', deals: byCloseDateAsc(deals.filter(isCommitted)) },
    { label: 'UPSIDE', slug: 'upside', deals: byCloseDateAsc(deals.filter(isUpside)) },
    {
      label: 'PIPELINE · NO POSITION',
      slug: 'pipeline',
      deals: byCloseDateAsc(deals.filter((d) => !isCommitted(d) && !isUpside(d))),
    },
  ];

  const rawTotal = deals.reduce((a, d) => a + d.raw_amount, 0);
  /** The section forecast call at the LIVE Upside % — the one comparable basis. */
  const forecastCall = split(deals, upsidePct).total;

  return (
    <div className="max-h-[28rem] overflow-x-auto overflow-y-auto">
      <table className="w-full text-sm" data-testid={`forecast-dealdetail-${sectionKey}`}>
        <thead className="sticky top-0 border-b border-ink-200 bg-white">
          <tr>
            <Th className="text-left">Opps</Th>
            <Th className="text-left">Type</Th>
            <Th className="text-right">Close</Th>
            <Th className="text-right">Amount</Th>
            <Th className="text-left">Position</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <TierGroup key={g.slug} label={g.label} slug={g.slug} deals={g.deals} sectionKey={sectionKey} />
          ))}
        </tbody>
        <tfoot>
          <tr
            className="border-t-2 border-ink-200 bg-white font-semibold"
            data-testid={`forecast-dealdetail-subtotal-${sectionKey}`}
          >
            <td className="px-3 py-2 text-ink-900" colSpan={3}>
              Subtotal
            </td>
            <td className="px-3 py-2 text-right text-ink-900">
              <span className="block text-[0.6rem] font-normal uppercase tracking-wider text-ink-400">
                Amount
              </span>
              <span className="tabular-nums">{formatAmount(rawTotal)}</span>
            </td>
            <td className="px-3 py-2 text-left text-ink-900">
              <span className="block text-[0.6rem] font-normal uppercase tracking-wider text-ink-400">
                Forecast call
              </span>
              <span
                className="tabular-nums"
                data-testid={`forecast-dealdetail-forecast-${sectionKey}`}
              >
                {formatAmount(forecastCall)}
              </span>
            </td>
          </tr>
          {target !== null && (
            <>
              <tr className="text-ink-500">
                <td className="px-3 py-1.5" colSpan={4}>
                  ARR budget
                </td>
                <td className="px-3 py-1.5 text-left">
                  <span
                    className="tabular-nums"
                    data-testid={`forecast-dealdetail-budget-${sectionKey}`}
                  >
                    {formatAmount(target)}
                  </span>
                </td>
              </tr>
              <tr className="text-ink-500">
                <td className="px-3 py-1.5" colSpan={4}>
                  Variance to budget
                </td>
                <td className="px-3 py-1.5 text-left">
                  <VarianceValue
                    delta={forecastCall - target}
                    testId={`forecast-dealdetail-variance-${sectionKey}`}
                  />
                </td>
              </tr>
            </>
          )}
        </tfoot>
      </table>
    </div>
  );
}

function TierGroup({
  label,
  slug,
  deals,
  sectionKey,
}: {
  label: string;
  slug: string;
  deals: ForecastDeal[];
  sectionKey: string;
}): React.JSX.Element | null {
  if (deals.length === 0) return null;
  return (
    <>
      <tr className="bg-ink-50/50">
        <td
          className="px-3 pb-1 pt-3 text-[0.68rem] font-semibold uppercase tracking-wider text-ink-500"
          colSpan={5}
          data-testid={`forecast-dealgroup-${sectionKey}-${slug}`}
        >
          {label}
        </td>
      </tr>
      {deals.map((d) => (
        <tr
          key={d.opp_id}
          className={`border-b border-ink-100 ${isWon(d) ? 'bg-good-bg' : 'hover:bg-ink-100'}`}
          data-band={isWon(d) ? 'closed_won' : undefined}
          data-testid={`forecast-deal-row-${d.opp_id}`}
        >
          <td className="px-3 py-2 text-ink-900">
            <DealName deal={d} />
          </td>
          <td className="px-3 py-2 text-ink-500">{typeLabel(d.type)}</td>
          <td className="px-3 py-2 text-right tabular-nums text-ink-500">
            {d.close_date === null ? '—' : formatCloseDate(d.close_date)}
          </td>
          <td className="px-3 py-2 text-right tabular-nums text-ink-900">
            {formatAmount(d.raw_amount)}
          </td>
          <td className="px-3 py-2 text-ink-500">{d.forecast_position}</td>
        </tr>
      ))}
    </>
  );
}

/**
 * quick-261015-lej — "Account — deal name" on a Managed Services row whose
 * account is known; the bare deal name everywhere else. Pinned as
 * `data-account` so tests lock the behaviour without freezing the styling.
 */
function DealName({ deal }: { deal: ForecastDeal }): React.JSX.Element {
  const prefix = dealAccountPrefix(deal);
  if (prefix === null) return <>{deal.name}</>;
  return (
    <>
      <span className="font-medium" data-account={prefix}>
        {prefix}
      </span>
      <span className="text-ink-400"> — </span>
      {deal.name}
    </>
  );
}

/** D-06: the signed over/under, so a shortfall carries a real minus sign. */
function VarianceValue({ delta, testId }: { delta: number; testId: string }): React.JSX.Element {
  const good = delta >= 0;
  return (
    <span
      className={`tabular-nums ${good ? 'text-good-text' : 'text-bad-text'}`}
      data-testid={testId}
    >
      {signed(delta)}
    </span>
  );
}

function Th({ className, children }: { className?: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <th
      className={`px-3 py-2 text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500 ${className ?? ''}`}
    >
      {children}
    </th>
  );
}
